import { Controller } from "@hotwired/stimulus"

// Bid Q&A tab (roadmap #56): question list + filters, ask modal with
// chip-based S3 uploads, action-items-style slide-over (private clarify chat +
// official answer), team tools (settings, hide, duplicate, publish addendum).
// All server responses carry role-scoped, pre-anonymized data — this
// controller never decides who an asker is, it only renders what it's given.
export default class extends Controller {
  static targets = [
    "row", "tableBody", "emptyState", "searchInput", "statusChips",
    "detailModal", "detailPanel", "detailContent", "detailHeader", "composer",
    "kebabWrap", "kebabMenu",
    "askModal", "askSubject", "askBody", "askDropzone", "askFileInput", "askChips", "askError", "askSubmit", "askButton",
    "settingsModal", "settingsDeadline", "answererCheckbox", "publicLink",
    "publishModal", "publishList", "publishCheckbox", "publishEmpty", "publishError", "publishSubmit", "publishCount",
    "duplicateModal", "duplicateSelect", "hideModal", "deadlineBanner"
  ]

  static values = {
    projectId: Number,
    isTeam: Boolean,
    canAnswer: Boolean,
    isBidder: Boolean,
    askingOpen: Boolean,
    createUrl: String,
    questionUrl: String,
    presignUrl: String,
    registerUrl: String,
    settingsUrl: String,
    publishUrl: String,
    i18n: { type: Object, default: {} }  // pages.bid_qa_js — falls back to the English below
  }

  connect() {
    this._statusFilter = "all"
    this._askFiles = []
    this._composerFiles = []
    this._answerFiles = []
    this._followUpOf = null
    this._current = null
    this._sending = false
  }

  // ── Filtering ─────────────────────────────────────────────────────────

  applyFilters() {
    const term = (this.hasSearchInputTarget ? this.searchInputTarget.value : "").toLowerCase().trim()
    this.rowTargets.forEach(row => {
      const matchesTerm = !term || (row.dataset.search || "").includes(term)
      let matchesStatus = true
      if (this._statusFilter === "hidden") {
        matchesStatus = row.dataset.hidden === "true"
      } else if (this._statusFilter !== "all") {
        matchesStatus = row.dataset.status === this._statusFilter && row.dataset.hidden !== "true"
      }
      row.classList.toggle("hidden", !(matchesTerm && matchesStatus))
    })
  }

  setStatusFilter(event) {
    this._statusFilter = event.currentTarget.dataset.filter
    this.statusChipsTarget.querySelectorAll("button").forEach(btn => {
      const active = btn.dataset.filter === this._statusFilter
      btn.classList.toggle("bg-gray-800", active)
      btn.classList.toggle("text-white", active)
      btn.classList.toggle("bg-gray-100", !active)
      btn.classList.toggle("text-gray-600", !active)
    })
    this.applyFilters()
  }

  // ── Slide-over detail ─────────────────────────────────────────────────

  async openDetail(event) {
    const qid = event.currentTarget.dataset.qid
    const data = await this._fetchJSON(this._questionUrl(qid))
    if (!data) return
    this._current = data
    this._composerFiles = []
    this._answerFiles = []
    this._renderDetail(data)
    this.detailModalTarget.classList.remove("hidden")
    requestAnimationFrame(() => this.detailPanelTarget.classList.remove("translate-x-full"))
  }

  closeDetail() {
    this.detailPanelTarget.classList.add("translate-x-full")
    setTimeout(() => this.detailModalTarget.classList.add("hidden"), 200)
    this._current = null
  }

  stopPropagation(event) {
    event.stopPropagation()
    if (this.hasKebabMenuTarget && !event.target.closest("[data-bid-qa-target='kebabWrap']")) {
      this.kebabMenuTarget.classList.add("hidden")
    }
  }

  toggleKebab(event) {
    event.stopPropagation()
    this.kebabMenuTarget.classList.toggle("hidden")
  }

  _renderDetail(q) {
    this.detailHeaderTarget.innerHTML = `
      <span class="text-[13px] font-semibold text-gray-500">${this._esc(q.code)}</span>
      ${this._statusPill(q)}
      ${q.hidden ? `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[13px] bg-gray-100 text-gray-500">${this._t("hidden", "Hidden")}</span>` : ""}
      ${q.addendum_number ? `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[13px] bg-blue-50 text-blue-600">${this._t("addendum_n", "Addendum %{number}", { number: q.addendum_number })}</span>` : ""}
    `
    this._renderKebab(q)

    let html = `<div class="px-4 py-4 space-y-4">`

    // Question block
    html += `<div>
      <div class="flex items-start justify-between gap-2">
        <h3 class="text-[15px] font-semibold text-gray-800" data-role="q-subject">${this._esc(q.subject)}</h3>
      </div>
      <div class="text-[13px] text-gray-400 mt-0.5">
        ${this._t("asked_by", "Asked by %{name}", { name: this._esc(q.asked_by_you ? this._t("you", "you") : q.asked_by) })} · ${this._esc(q.asked_at)}${q.edited ? ` · ${this._t("edited", "edited")}` : ""}
        ${q.follow_up_of ? ` · ${this._t("follow_up_to", "follow-up to %{code}", { code: `<b>${this._esc(q.follow_up_of.code)}</b>` })}` : ""}
      </div>
      ${q.body ? `<div class="text-[13px] text-gray-700 whitespace-pre-wrap mt-2" data-role="q-body">${this._esc(q.body)}</div>` : ""}
      ${this._attachmentChips(q.attachments)}
    </div>`

    // Edit form (hidden until toggled)
    if (q.can_edit) {
      html += `<div class="hidden space-y-2" data-role="edit-form">
        <input type="text" value="${this._escAttr(q.subject)}" data-role="edit-subject" class="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-[13px]">
        <textarea rows="4" data-role="edit-body" class="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-[13px]">${this._esc(q.body || "")}</textarea>
        <div class="flex justify-end gap-2">
          <button type="button" class="px-2.5 py-1 rounded-lg text-[13px] text-gray-600 hover:bg-gray-100" data-action="click->bid-qa#cancelEdit">${this._t("cancel", "Cancel")}</button>
          <button type="button" class="px-2.5 py-1 rounded-lg text-[13px] bg-blue-600 hover:bg-blue-700 text-white" data-action="click->bid-qa#saveEdit">${this._t("save_changes", "Save changes")}</button>
        </div>
      </div>`
    }

    // Duplicate pointer
    if (q.duplicate_of) {
      html += `<div class="px-3 py-2 rounded-lg bg-gray-50 text-[13px] text-gray-600">
        ${this._t("duplicate_of", "Marked as a duplicate of %{original}. The answer below is the original's.", { original: `<b>${this._esc(q.duplicate_of.code)} — ${this._esc(q.duplicate_of.subject)}</b>` })}
      </div>`
    }

    // Answer block
    if (q.answer) {
      html += `<div class="rounded-lg border border-green-200 bg-green-50 px-3 py-3" data-role="answer-block">
        <div class="flex items-center justify-between">
          <div class="text-[13px] font-semibold text-green-800">${this._t("official_answer", "Official answer")}</div>
          <div class="text-[13px] text-green-700">${this._esc(q.answer.by || this._t("team", "Team"))} · ${this._esc(q.answer.at || "")}${q.answer.revised ? ` · ${this._t("corrected", "corrected")}` : ""}</div>
        </div>
        <div class="text-[13px] text-gray-800 whitespace-pre-wrap mt-1.5">${this._esc(q.answer.body)}</div>
        ${this._attachmentChips(q.answer.attachments)}
      </div>`
    } else if (q.status === "open") {
      html += `<div class="px-3 py-2 rounded-lg bg-amber-50 text-[13px] text-amber-700">
        ${this._t("waiting_answer", "Waiting for the official answer.")} ${this.isTeamValue ? "" : this._t("email_when_posted", "You'll get an email the moment it's posted.")}
      </div>`
    }

    if (q.can_follow_up) {
      html += `<div><button type="button" class="text-[13px] text-blue-600 hover:underline font-medium" data-action="click->bid-qa#startFollowUp">${this._t("ask_follow_up", "Ask a follow-up question")}</button></div>`
    }

    // Private thread
    if (q.can_chat) {
      html += `<div>
        <div class="flex items-center gap-2 mt-2 mb-1">
          <span class="text-[13px] font-semibold text-gray-700">${this._t("private_thread", "Private thread")}</span>
          <span class="text-[13px] text-gray-400">${this._t("private_thread_hint", "only the asker and the project team see this")}</span>
        </div>
        <div class="space-y-2" data-role="thread">${this._threadHtml(q.messages || [])}</div>
      </div>`
    }

    html += `</div>`
    this.detailContentTarget.innerHTML = html
    this._renderComposer(q)
  }

  _threadHtml(messages) {
    if (!messages.length) {
      return `<div class="text-[13px] text-gray-400">${this._t("no_messages", "No messages yet. Use the box below to clarify before the official answer goes out.")}</div>`
    }
    return messages.map(m => `
      <div class="flex ${m.mine ? "justify-end" : "justify-start"}">
        <div class="max-w-[85%] rounded-lg px-3 py-2 ${m.from_team ? "bg-blue-50" : "bg-gray-100"}">
          <div class="text-[13px] text-gray-500">${this._esc(m.author)} · ${this._esc(m.at)}</div>
          ${m.body ? `<div class="text-[13px] text-gray-800 whitespace-pre-wrap">${this._esc(m.body)}</div>` : ""}
          ${this._attachmentChips(m.attachments)}
        </div>
      </div>
    `).join("")
  }

  _attachmentChips(attachments) {
    if (!attachments || !attachments.length) return ""
    const chips = attachments.map(a => `
      <a href="${this._escAttr(a.url)}" target="_blank" class="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-[13px] text-gray-700">
        ${this._docIcon()} ${this._esc(a.filename)}
      </a>`).join("")
    return `<div class="flex flex-wrap gap-1.5 mt-2">${chips}</div>`
  }

  _renderKebab(q) {
    const items = []
    if (q.can_edit) items.push(`<button type="button" class="block w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50" data-action="click->bid-qa#startEdit">${this._t("edit_question", "Edit question")}</button>`)
    if (this.isTeamValue) {
      if (q.hidden) {
        items.push(`<button type="button" class="block w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50" data-action="click->bid-qa#unhide">${this._t("unhide_question", "Unhide question")}</button>`)
      } else {
        items.push(`<button type="button" class="block w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50" data-action="click->bid-qa#openHideModal">${this._t("hide_question", "Hide question")}</button>`)
      }
      if (q.status === "duplicate") {
        items.push(`<button type="button" class="block w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50" data-action="click->bid-qa#unmarkDuplicate">${this._t("unmark_duplicate", "Unmark duplicate")}</button>`)
      } else {
        items.push(`<button type="button" class="block w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50" data-action="click->bid-qa#openDuplicateModal">${this._t("mark_as_duplicate", "Mark as duplicate")}</button>`)
      }
      if (q.answer && q.can_answer && !q.duplicate_of) {
        items.push(`<button type="button" class="block w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50" data-action="click->bid-qa#startCorrection">${this._t("correct_answer", "Correct the answer")}</button>`)
      }
    }
    this.kebabWrapTarget.classList.toggle("hidden", items.length === 0)
    this.kebabMenuTarget.innerHTML = items.join("")
    this.kebabMenuTarget.classList.add("hidden")
  }

  _renderComposer(q) {
    const el = this.composerTarget
    const showAnswerBox = q.can_answer && !q.answer && q.status !== "duplicate"

    if (showAnswerBox) {
      el.innerHTML = this._answerComposerHtml(this._t("post_official_answer", "Post the official answer"), this._t("post_answer_hint", "This goes to every bidder by email and can be published as an addendum."))
    } else if (q.can_chat) {
      el.innerHTML = `
        <div class="flex flex-wrap gap-1.5 mb-1.5" data-role="composer-chips"></div>
        <div class="flex items-end gap-2">
          <button type="button" class="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="${this._t("attach_files", "Attach files")}" data-action="click->bid-qa#pickComposerFiles">${this._clipIcon()}</button>
          <textarea rows="1" placeholder="${this._t("write_private_message", "Write a private message...")}" data-role="composer-input"
                    class="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-[13px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                    data-action="keydown->bid-qa#composerKeydown"></textarea>
          <button type="button" class="px-3 py-2 rounded-lg text-[13px] font-medium bg-blue-600 hover:bg-blue-700 text-white" data-action="click->bid-qa#sendChat">${this._t("send", "Send")}</button>
        </div>
        <input type="file" multiple class="hidden" data-role="composer-file-input" data-action="change->bid-qa#composerFilesPicked">
        ${q.can_answer && !q.duplicate_of ? `<div class="mt-2 hidden" data-role="answer-area">${this._answerComposerHtml(this._t("save_correction", "Save correction"), this._t("correction_hint", "Bidders get a correction email — the new answer replaces the old one."))}</div>` : ""}
      `
    } else {
      el.innerHTML = ""
    }
    el.classList.toggle("hidden", el.innerHTML.trim() === "")
  }

  _answerComposerHtml(buttonLabel, hint) {
    return `
      <div class="rounded-lg border border-green-300 bg-green-50/50 p-2.5" data-role="answer-composer">
        <div class="text-[13px] font-semibold text-green-800 mb-1">${this._t("official_answer", "Official answer")}</div>
        <textarea rows="3" placeholder="${this._t("write_answer", "Write the answer all bidders will receive...")}" data-role="answer-input"
                  class="w-full border border-gray-300 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-green-500"></textarea>
        <div class="flex flex-wrap gap-1.5 my-1.5" data-role="answer-chips"></div>
        <div class="flex items-center justify-between">
          <button type="button" class="inline-flex items-center gap-1 text-[13px] text-gray-500 hover:text-gray-700" data-action="click->bid-qa#pickAnswerFiles">${this._clipIcon()} ${this._t("attach_files", "Attach files")}</button>
          <div class="flex items-center gap-2">
            <span class="text-[13px] text-gray-400 hidden sm:inline">${hint}</span>
            <button type="button" class="px-3 py-1.5 rounded-lg text-[13px] font-medium bg-green-600 hover:bg-green-700 text-white" data-action="click->bid-qa#postAnswer">${buttonLabel}</button>
          </div>
        </div>
        <input type="file" multiple class="hidden" data-role="answer-file-input" data-action="change->bid-qa#answerFilesPicked">
      </div>`
  }

  // ── Detail actions ────────────────────────────────────────────────────

  startEdit() {
    this.kebabMenuTarget.classList.add("hidden")
    const form = this.detailContentTarget.querySelector("[data-role='edit-form']")
    if (form) form.classList.remove("hidden")
  }

  cancelEdit() {
    const form = this.detailContentTarget.querySelector("[data-role='edit-form']")
    if (form) form.classList.add("hidden")
  }

  async saveEdit() {
    const subject = this.detailContentTarget.querySelector("[data-role='edit-subject']")?.value?.trim()
    const body = this.detailContentTarget.querySelector("[data-role='edit-body']")?.value ?? ""
    if (!subject) return this._showToast(this._t("subject_blank", "Subject can't be blank."), "alert")

    const data = await this._fetchJSON(this._questionUrl(this._current.id), {
      method: "PATCH", body: { subject: subject, body: body }
    })
    if (!data) return
    this._afterQuestionUpdate(data, this._t("question_updated", "Question updated."))
  }

  startCorrection() {
    this.kebabMenuTarget.classList.add("hidden")
    const area = this.composerTarget.querySelector("[data-role='answer-area']")
    if (area) {
      area.classList.remove("hidden")
      const input = area.querySelector("[data-role='answer-input']")
      if (input && this._current?.answer) input.value = this._current.answer.body
      input?.focus()
    }
  }

  async postAnswer() {
    const input = this.composerTarget.querySelector("[data-role='answer-input']")
    const body = input?.value?.trim()
    if (!body) return this._showToast(this._t("answer_blank", "Answer can't be blank."), "alert")
    if (this._sending) return
    this._sending = true

    const data = await this._fetchJSON(this._questionUrl(this._current.id) + "/answer", {
      method: "POST", body: { body: body, attachment_ids: this._answerFiles.filter(f => f.id).map(f => f.id) }
    })
    this._sending = false
    if (!data) return

    this._answerFiles = []
    this._afterQuestionUpdate(data, this._t("answer_sent", "Answer sent to all bidders."))
    this._addToPublishList(data.question)
  }

  composerKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.sendChat()
    }
  }

  async sendChat() {
    const input = this.composerTarget.querySelector("[data-role='composer-input']")
    const body = input?.value?.trim() ?? ""
    const fileIds = this._composerFiles.filter(f => f.id).map(f => f.id)
    if (!body && !fileIds.length) return
    if (this._sending) return
    this._sending = true

    const data = await this._fetchJSON(this._questionUrl(this._current.id) + "/messages", {
      method: "POST", body: { body: body, attachment_ids: fileIds }
    })
    this._sending = false
    if (!data) return

    this._composerFiles = []
    const chips = this.composerTarget.querySelector("[data-role='composer-chips']")
    if (chips) chips.innerHTML = ""
    if (input) input.value = ""
    this._current = data.question
    const thread = this.detailContentTarget.querySelector("[data-role='thread']")
    if (thread) thread.innerHTML = this._threadHtml(data.question.messages || [])
  }

  async unhide() {
    this.kebabMenuTarget.classList.add("hidden")
    const data = await this._fetchJSON(this._questionUrl(this._current.id) + "/unhide", { method: "POST" })
    if (data) this._afterQuestionUpdate(data, this._t("question_visible", "Question is visible to bidders again."))
  }

  openHideModal() {
    this.kebabMenuTarget.classList.add("hidden")
    this.hideModalTarget.classList.remove("hidden")
  }

  async confirmHide() {
    const data = await this._fetchJSON(this._questionUrl(this._current.id) + "/hide", { method: "POST" })
    this.hideModalTarget.classList.add("hidden")
    if (data) this._afterQuestionUpdate(data, this._t("question_hidden", "Question hidden from bidders."))
  }

  openDuplicateModal() {
    this.kebabMenuTarget.classList.add("hidden")
    const select = this.duplicateSelectTarget
    select.innerHTML = ""
    this.rowTargets.forEach(row => {
      const qid = parseInt(row.dataset.qid, 10)
      if (qid === this._current.id) return
      if (row.dataset.status === "duplicate") return
      const code = row.querySelector("td")?.innerText?.trim() || `#${qid}`
      const subject = row.querySelector("td:nth-child(2) span")?.innerText?.trim() || ""
      const opt = document.createElement("option")
      opt.value = qid
      opt.textContent = `${code} — ${subject}`.slice(0, 90)
      select.appendChild(opt)
    })
    this.duplicateModalTarget.classList.remove("hidden")
  }

  async confirmDuplicate() {
    const originalId = this.duplicateSelectTarget.value
    if (!originalId) return
    const data = await this._fetchJSON(this._questionUrl(this._current.id) + "/duplicate", {
      method: "POST", body: { duplicate_of_id: originalId }
    })
    this.duplicateModalTarget.classList.add("hidden")
    if (data) this._afterQuestionUpdate(data, this._t("marked_duplicate", "Marked as duplicate."))
  }

  async unmarkDuplicate() {
    this.kebabMenuTarget.classList.add("hidden")
    const data = await this._fetchJSON(this._questionUrl(this._current.id) + "/duplicate", {
      method: "POST", body: { duplicate_of_id: "" }
    })
    if (data) this._afterQuestionUpdate(data, this._t("duplicate_removed", "Duplicate mark removed."))
  }

  startFollowUp() {
    this._followUpOf = this._current.id
    const code = this._current.code
    const subject = this._current.subject
    this.closeDetail()
    this.openAsk()
    this.askSubjectTarget.value = this._t("follow_up_subject", "Follow-up to %{code}: %{subject}", { code, subject }).slice(0, 250)
    this.askBodyTarget.focus()
  }

  _afterQuestionUpdate(data, toastMessage) {
    if (data.row_html) this._swapRow(data.question.id, data.row_html)
    if (data.question) {
      this._current = data.question
      this._renderDetail(data.question)
    }
    if (toastMessage) this._showToast(toastMessage, "notice")
  }

  // ── Ask modal ─────────────────────────────────────────────────────────

  openAsk() {
    if (!this.askingOpenValue && !this.isTeamValue) {
      this._showToast(this._t("window_closed", "The question window for this bid has closed."), "alert")
      return
    }
    this.askModalTarget.classList.remove("hidden")
    this.askSubjectTarget.focus()
  }

  closeModals() {
    ["askModal", "settingsModal", "publishModal", "duplicateModal", "hideModal"].forEach(name => {
      const target = `${name}Target`
      if (this[`has${name.charAt(0).toUpperCase() + name.slice(1)}Target`] && this[target]) {
        this[target].classList.add("hidden")
      }
    })
    if (this.hasAskErrorTarget) this.askErrorTarget.classList.add("hidden")
    this._followUpOf = null
  }

  async submitAsk() {
    const subject = this.askSubjectTarget.value.trim()
    const body = this.askBodyTarget.value.trim()
    if (!subject) {
      this.askErrorTarget.textContent = this._t("give_subject", "Please give your question a subject.")
      this.askErrorTarget.classList.remove("hidden")
      return
    }
    if (this._askFiles.some(f => f.uploading)) {
      this.askErrorTarget.textContent = this._t("files_uploading", "Files are still uploading — one moment.")
      this.askErrorTarget.classList.remove("hidden")
      return
    }
    if (this._sending) return
    this._sending = true
    this.askSubmitTarget.disabled = true

    const payload = { subject: subject, body: body, attachment_ids: this._askFiles.filter(f => f.id).map(f => f.id) }
    const url = this._followUpOf ? this._questionUrl(this._followUpOf) + "/follow_up" : this.createUrlValue
    const data = await this._fetchJSON(url, { method: "POST", body: payload })

    this._sending = false
    this.askSubmitTarget.disabled = false
    if (!data) return

    this._swapRow(data.question.id, data.row_html, { prepend: true })
    this.askSubjectTarget.value = ""
    this.askBodyTarget.value = ""
    this.askChipsTarget.innerHTML = ""
    this._askFiles = []
    this._followUpOf = null
    this.closeModals()
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    this._showToast(this._t("question_submitted", "Question %{code} submitted.", { code: data.question.code }), "notice")
  }

  // ── Settings ──────────────────────────────────────────────────────────

  openSettings() {
    this.settingsModalTarget.classList.remove("hidden")
  }

  copyPublicLink() {
    const input = this.publicLinkTarget
    input.select()
    navigator.clipboard?.writeText(input.value)
    this._showToast(this._t("link_copied", "Link copied."), "notice")
  }

  async saveSettings() {
    const ids = this.answererCheckboxTargets.filter(cb => cb.checked).map(cb => cb.value)
    const data = await this._fetchJSON(this.settingsUrlValue, {
      method: "POST",
      body: { deadline: this.settingsDeadlineTarget.value, answerer_ids: ids }
    })
    if (!data) return
    this.askingOpenValue = data.asking_open
    if (this.hasDeadlineBannerTarget) {
      const label = data.deadline
        ? this._t("deadline_banner", "Questions are open until %{date} (end of day). Answers are shared with all bidders.", { date: data.deadline_label || data.deadline })
        : this._t("no_deadline_banner", "No question deadline set — bidders can ask while bidding is open.")
      this.deadlineBannerTarget.querySelector("div").lastChild.textContent = " " + label
    }
    this.closeModals()
    this._showToast(this._t("settings_saved", "Q&A settings saved."), "notice")
  }

  // ── Publish addendum ──────────────────────────────────────────────────

  openPublish() {
    this.publishModalTarget.classList.remove("hidden")
  }

  async submitPublish() {
    const ids = this.publishCheckboxTargets.filter(cb => cb.checked).map(cb => cb.value)
    if (!ids.length) {
      this.publishErrorTarget.textContent = this._t("pick_one", "Pick at least one answered question.")
      this.publishErrorTarget.classList.remove("hidden")
      return
    }
    this.publishSubmitTarget.disabled = true
    const data = await this._fetchJSON(this.publishUrlValue, { method: "POST", body: { question_ids: ids } })
    this.publishSubmitTarget.disabled = false
    if (!data) return

    if (data.rows_html) {
      Object.entries(data.rows_html).forEach(([qid, html]) => this._swapRow(qid, html))
    }
    // Remove published entries from the modal list + badge count.
    ids.forEach(qid => this.publishListTarget.querySelector(`[data-publish-qid="${qid}"]`)?.remove())
    this._refreshPublishCount()
    this.closeModals()
    this._showToast(this._t("addendum_published", "Addendum %{number} published — all bidders notified.", { number: data.addendum_number }), "notice")
  }

  _addToPublishList(question) {
    if (!this.hasPublishListTarget || !question) return
    if (this.publishListTarget.querySelector(`[data-publish-qid="${question.id}"]`)) return
    const label = document.createElement("label")
    label.className = "flex items-start gap-2 cursor-pointer text-[13px] text-gray-700"
    label.dataset.publishQid = question.id
    label.innerHTML = `<input type="checkbox" value="${question.id}" checked class="rounded mt-0.5" data-bid-qa-target="publishCheckbox"><span><b>${this._esc(question.code)}</b> — ${this._esc(question.subject)}</span>`
    this.publishListTarget.appendChild(label)
    if (this.hasPublishEmptyTarget) this.publishEmptyTarget.classList.add("hidden")
    this._refreshPublishCount()
  }

  _refreshPublishCount() {
    if (!this.hasPublishCountTarget) return
    const count = this.publishListTarget.querySelectorAll("[data-publish-qid]").length
    this.publishCountTarget.textContent = count
    this.publishCountTarget.classList.toggle("hidden", count === 0)
  }

  // ── File uploads (presign → S3 PUT → register) ────────────────────────

  pickAskFiles() { this.askFileInputTarget.click() }
  askFilesPicked(event) { this._uploadFiles([...event.target.files], "question", this.askChipsTarget, this._askFiles); event.target.value = "" }

  dragOver(event) {
    event.preventDefault()
    this.askDropzoneTarget.classList.add("border-blue-400", "bg-blue-50")
  }

  dragLeave() {
    this.askDropzoneTarget.classList.remove("border-blue-400", "bg-blue-50")
  }

  dropAskFiles(event) {
    event.preventDefault()
    this.dragLeave()
    this._uploadFiles([...event.dataTransfer.files], "question", this.askChipsTarget, this._askFiles)
  }

  pickComposerFiles() { this.composerTarget.querySelector("[data-role='composer-file-input']")?.click() }
  composerFilesPicked(event) {
    const chips = this.composerTarget.querySelector("[data-role='composer-chips']")
    this._uploadFiles([...event.target.files], "message", chips, this._composerFiles)
    event.target.value = ""
  }

  pickAnswerFiles() { this.composerTarget.querySelector("[data-role='answer-file-input']")?.click() }
  answerFilesPicked(event) {
    const chips = this.composerTarget.querySelector("[data-role='answer-chips']")
    this._uploadFiles([...event.target.files], "message", chips, this._answerFiles)
    event.target.value = ""
  }

  async _uploadFiles(files, target, chipsEl, list) {
    if (!files.length || !chipsEl) return

    const entries = files.map(file => {
      const chip = this._createChip(chipsEl, file.name)
      const entry = { file, chip, uploading: true, id: null }
      list.push(entry)
      return entry
    })

    let presigned
    try {
      const res = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrf(), "Content-Type": "application/json" },
        body: JSON.stringify({ files: entries.map(e => ({ filename: e.file.name, content_type: e.file.type || "application/octet-stream", byte_size: e.file.size })) })
      })
      presigned = await res.json()
      if (!res.ok) throw new Error(presigned.error || this._t("upload_failed", "Upload failed."))
    } catch (err) {
      entries.forEach(e => { e.chip.remove(); list.splice(list.indexOf(e), 1) })
      this._showToast(err.message || this._t("upload_failed", "Upload failed."), "alert")
      return
    }

    await Promise.all(presigned.uploads.map(async (upload, i) => {
      const entry = entries[i]
      try {
        const putRes = await fetch(upload.upload_url, {
          method: "PUT",
          headers: { "Content-Type": entry.file.type || upload.content_type },
          body: entry.file
        })
        if (!putRes.ok) throw new Error(`${this._t("upload_failed", "Upload failed.")} (${putRes.status})`)

        const regRes = await fetch(`${this.registerUrlValue}?target=${target}`, {
          method: "POST",
          headers: { "X-CSRF-Token": this._csrf(), "Content-Type": "application/json" },
          body: JSON.stringify({ files: [{ cache_name: upload.cache_name, filename: upload.filename }] })
        })
        const registered = await regRes.json()
        if (!regRes.ok || !registered[0]) throw new Error(this._t("upload_failed", "Upload failed."))

        entry.id = registered[0].id
        entry.uploading = false
        this._finishChip(entry, list)
      } catch (err) {
        entry.chip.remove()
        list.splice(list.indexOf(entry), 1)
        this._showToast(`${entry.file.name}: ${err.message}`, "alert")
      }
    }))
  }

  _createChip(chipsEl, filename) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-50 text-blue-700 text-[13px]"
    chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg><span>${this._esc(filename)}</span>`
    chipsEl.appendChild(chip)
    return chip
  }

  _finishChip(entry, list) {
    const chip = entry.chip
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-[13px]"
    chip.innerHTML = `${this._docIcon()}<span>${this._esc(entry.file.name)}</span>`
    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "text-gray-400 hover:text-gray-600"
    remove.innerHTML = "&times;"
    remove.addEventListener("click", () => {
      chip.remove()
      const idx = list.indexOf(entry)
      if (idx >= 0) list.splice(idx, 1)
    })
    chip.appendChild(remove)
  }

  // ── Plumbing ──────────────────────────────────────────────────────────

  _questionUrl(qid) {
    return this.questionUrlValue.replace(/\/0(?=$|\?)/, `/${qid}`)
  }

  _swapRow(qid, rowHtml, { prepend = false } = {}) {
    if (!rowHtml) return
    const existing = document.getElementById(`bid-question-row-${qid}`)
    const tpl = document.createElement("template")
    tpl.innerHTML = rowHtml.trim()
    const fresh = tpl.content.firstElementChild
    if (existing) {
      existing.replaceWith(fresh)
    } else if (prepend) {
      this.tableBodyTarget.prepend(fresh)
    } else {
      this.tableBodyTarget.appendChild(fresh)
    }
    this.applyFilters()
  }

  async _fetchJSON(url, { method = "GET", body = null } = {}) {
    try {
      const res = await fetch(url, {
        method,
        headers: { "X-CSRF-Token": this._csrf(), "Content-Type": "application/json", "Accept": "application/json" },
        body: body ? JSON.stringify(body) : null
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        this._showToast(data.error || this._t("something_wrong", "Something went wrong — please try again."), "alert")
        return null
      }
      return data
    } catch {
      this._showToast(this._t("network_error", "Network error — please try again."), "alert")
      return null
    }
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  _showToast(message, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }

  // Text comes from our own locale files; vars may carry escaped HTML.
  _t(key, fallback, vars = {}) {
    const text = (this.i18nValue && this.i18nValue[key]) || fallback
    return text.replace(/%\{(\w+)\}/g, (_, name) => (vars[name] ?? ""))
  }

  _statusPill(q) {
    if (q.status === "duplicate") {
      return `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] bg-gray-100 text-gray-500">${this._t("status_duplicate", "Duplicate")}</span>`
    }
    if (q.status === "answered") {
      return `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] bg-green-100 text-green-700">${this._t("status_answered", "Answered")}</span>`
    }
    return `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] bg-amber-100 text-amber-700">${this._t("status_open", "Open")}</span>`
  }

  _docIcon() {
    return `<svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>`
  }

  _clipIcon() {
    return `<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"/></svg>`
  }

  _esc(str) {
    if (str === null || str === undefined) return ""
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _escAttr(str) {
    return this._esc(str)
  }
}
